import { KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ContextTokenWarningDialog from './ContextTokenWarningDialog';
import MarkdownRenderer from './MarkdownRenderer';
import ThinkingIndicator from './ThinkingIndicator';
import { type ChatMessage, type ToolCallEntry, useAppStore } from '../stores/appStore';
import { useSettingsStore } from '../stores/settingsStore';
import { useFileEditorStore } from '../stores/fileEditorStore';
import { gatewayRef } from '../utils/gatewayRef';
import { toFriendlyErrorMessage } from '../utils/friendlyErrors';
import { getSessionTokenCount, shouldShowContextTokenWarning } from '../utils/contextTokenWarning';

const COCO_AGENT_ID = 'coco';
const COCO_FALLBACK_EMOJI = '🎮';
const COCO_FALLBACK_NAME = 'Coco';
const COCO_UNAVAILABLE_MESSAGE = "🎮 Coco isn't available. Check agent configuration in the Workshop.";

const COCO_CHAT_BAR_STYLES = `
.coco-chat-backdrop {
  position: fixed;
  inset: 0;
  z-index: 110;
  background: rgba(0, 0, 0, 0.4);
  opacity: 0;
  visibility: hidden;
  transition: opacity 240ms ease, visibility 240ms ease;
}

.coco-chat-backdrop.is-visible {
  opacity: 1;
  visibility: visible;
}

.coco-chat-shell {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 120;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  pointer-events: none;
}

.coco-chat-shell > * {
  pointer-events: auto;
}

.coco-chat-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  min-height: 52px;
  padding: 0.8rem 1rem;
  border-radius: 999px;
  border: 1px solid var(--theme-border);
  background: var(--theme-card);
  color: var(--theme-foreground);
  box-shadow: 0 4px 24px rgba(0,0,0,0.3);
  transition: transform 240ms ease, opacity 240ms ease, background 180ms ease, border-color 180ms ease;
}

.coco-chat-pill:hover {
  background: var(--theme-secondary);
  border-color: var(--theme-primary-20);
}

.coco-chat-pill.is-hidden {
  opacity: 0;
  transform: translateY(8px) scale(0.96);
  pointer-events: none;
}

.coco-chat-pill-label {
  font-family: var(--app-ui-font);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.coco-chat-pill-emoji {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: var(--theme-accent);
}

.coco-chat-pill-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: var(--theme-primary);
  animation: pulse-dot 1s ease-in-out infinite;
}

.coco-chat-panel {
  width: min(360px, calc(100vw - 32px));
  height: 480px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 18px;
  border: 1px solid var(--theme-border);
  background: var(--theme-card);
  box-shadow: 0 4px 24px rgba(0,0,0,0.3);
  transform-origin: bottom right;
  transform: translateY(16px) scale(0.97);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: transform 240ms ease, opacity 240ms ease, visibility 240ms ease;
}

.coco-chat-panel.is-open {
  transform: translateY(0) scale(1);
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.coco-chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid var(--theme-border);
  background: var(--theme-card);
}

.coco-chat-title {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.coco-chat-title-main {
  font-family: var(--app-ui-font);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--theme-foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.coco-chat-title-sub {
  margin-top: 0.18rem;
  font-family: var(--app-ui-font);
  font-size: 0.58rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--theme-muted-foreground);
}

.coco-chat-fold-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--theme-muted-foreground);
  transition: background 180ms ease, color 180ms ease, border-color 180ms ease;
}

.coco-chat-fold-button:hover {
  background: var(--theme-secondary);
  color: var(--theme-foreground);
  border-color: var(--theme-border);
}

.coco-chat-messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.coco-chat-empty,
.coco-chat-notice {
  border: 1px solid var(--theme-border);
  border-radius: 14px;
  background: var(--theme-secondary);
  color: var(--theme-card-foreground);
  padding: 0.9rem 1rem;
  font-family: var(--app-ui-font);
  font-size: 0.68rem;
  line-height: 1.6;
}

.coco-chat-empty-title {
  color: var(--theme-foreground);
  font-size: 0.76rem;
  font-weight: 700;
  margin-bottom: 0.35rem;
}

.coco-chat-row {
  display: flex;
}

.coco-chat-row.is-user {
  justify-content: flex-end;
}

.coco-chat-row.is-agent {
  justify-content: flex-start;
}

.coco-chat-row.is-system {
  justify-content: center;
}

.coco-chat-bubble-wrap {
  max-width: 100%;
}

.coco-chat-sender {
  margin-bottom: 0.35rem;
  padding: 0 0.2rem;
  font-family: var(--app-ui-font);
  font-size: 0.56rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.coco-chat-row.is-user .coco-chat-sender {
  color: var(--theme-muted-foreground);
  text-align: right;
}

.coco-chat-row.is-agent .coco-chat-sender {
  color: var(--theme-primary);
}

.coco-chat-bubble {
  border-radius: 14px;
  padding: 0.8rem 0.95rem;
  line-height: 1.55;
  word-break: break-word;
}

.coco-chat-row.is-user .coco-chat-bubble {
  background: var(--theme-user-bubble-background);
  color: var(--theme-user-bubble-text);
}

.coco-chat-row.is-agent .coco-chat-bubble {
  background: var(--theme-assistant-bubble-background);
  color: var(--theme-assistant-bubble-text);
}

.coco-chat-row.is-system .coco-chat-bubble {
  background: var(--theme-secondary);
  color: var(--theme-card-foreground);
  border: 1px solid var(--theme-border);
  font-family: var(--app-ui-font);
  font-size: 0.64rem;
}

.coco-chat-typing {
  display: inline-flex;
  align-items: center;
  gap: 0.32rem;
  min-height: 14px;
}

.coco-chat-typing span {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: var(--theme-primary);
  animation: coco-chat-bounce 1.1s ease-in-out infinite;
}

.coco-chat-typing span:nth-child(2) {
  animation-delay: 0.12s;
}

.coco-chat-typing span:nth-child(3) {
  animation-delay: 0.24s;
}

.coco-chat-composer {
  display: flex;
  gap: 0.7rem;
  align-items: end;
  padding: 0.9rem 1rem;
  border-top: 1px solid var(--theme-border);
  background: var(--theme-card);
}

.coco-chat-input {
  flex: 1;
  min-width: 0;
  min-height: 42px;
  max-height: 110px;
  resize: none;
  border-radius: 12px;
  border: 1px solid var(--theme-border);
  background: var(--theme-input);
  color: var(--theme-foreground);
  padding: 0.72rem 0.85rem;
  font-family: var(--app-ui-font);
  font-size: 0.72rem;
  line-height: 1.45;
  outline: none;
  transition: border-color 180ms ease, box-shadow 180ms ease;
}

.coco-chat-input:focus {
  border-color: var(--theme-primary-20);
  box-shadow: 0 0 0 2px var(--theme-accent);
}

.coco-chat-input:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background: var(--theme-secondary);
  color: var(--theme-muted-foreground);
}

.coco-chat-send {
  height: 42px;
  flex-shrink: 0;
  border: none;
  border-radius: 12px;
  padding: 0 0.95rem;
  background: var(--theme-primary);
  color: var(--theme-primary-foreground);
  font-family: var(--app-ui-font);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  transition: opacity 180ms ease, transform 180ms ease, background 180ms ease;
}

.coco-chat-send:hover:not(:disabled) {
  background: var(--theme-primary-hover);
}

.coco-chat-send:active:not(:disabled) {
  transform: translateY(1px);
}

.coco-chat-send:disabled {
  opacity: 0.75;
  cursor: not-allowed;
  background: var(--theme-secondary);
  color: var(--theme-muted-foreground);
}

@keyframes coco-chat-bounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-4px); opacity: 1; }
}

.coco-tool-calls {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.5rem 0;
}

.coco-tool-call {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.7rem;
  border-radius: 10px;
  background: var(--theme-secondary);
  border: 1px solid var(--theme-border);
  font-family: var(--app-ui-font);
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.coco-tool-call.is-running {
  border-left: 2px solid #FF7B35;
}

.coco-tool-call.is-done {
  border-left: 2px solid #3DFFA8;
}

.coco-tool-call.is-error {
  border-left: 2px solid #ef4444;
}

.coco-tool-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--theme-foreground);
  text-transform: uppercase;
}

.coco-tool-status {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  flex-shrink: 0;
  font-size: 0.56rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.coco-tool-status.is-running {
  color: #FF7B35;
}

.coco-tool-status.is-done {
  color: #3DFFA8;
}

.coco-tool-status.is-error {
  color: #ef4444;
}

.coco-tool-running-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #FF7B35;
  animation: pulse-dot 1s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 0.4; transform: scale(0.9); }
  50% { opacity: 1; transform: scale(1.1); }
}

@media (max-width: 1024px) {
  .coco-chat-shell {
    right: 16px;
    bottom: calc(68px + env(safe-area-inset-bottom, 0px));
  }
}

@media (max-width: 768px) {
  .coco-chat-shell {
    left: 12px;
    right: 12px;
    align-items: stretch;
  }

  .coco-chat-panel {
    width: auto;
    height: min(50vh, 460px);
    transform-origin: bottom center;
  }

  .coco-chat-pill {
    align-self: flex-end;
    min-height: 46px;
    padding: 0.7rem 0.9rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .coco-chat-panel,
  .coco-chat-pill,
  .coco-chat-fold-button,
  .coco-chat-send,
  .coco-chat-input {
    transition: none;
  }

  .coco-chat-typing span,
  .coco-chat-pill-dot {
    animation: none;
  }
}
`;

function sanitizeSessionSlug(value: string | null | undefined): string {
  const normalized = (value ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

  return normalized || 'main';
}

function createCocoSessionKey(userIdentifier: string): string {
  return `agent:${COCO_AGENT_ID}:dashboard-${sanitizeSessionSlug(userIdentifier)}`;
}

function deriveDashboardUserIdentifier(displayName: string, activeSessionKey: string | null): string {
  const trimmedDisplayName = displayName.trim();
  if (trimmedDisplayName) {
    return trimmedDisplayName;
  }

  if (activeSessionKey) {
    const [, , ...rest] = activeSessionKey.split(':');
    const sessionSuffix = rest.join(' ').trim();
    if (sessionSuffix) {
      return sessionSuffix;
    }
  }

  return 'main';
}

function toMessageRole(role: unknown): ChatMessage['role'] {
  if (role === 'user' || role === 'assistant' || role === 'system') {
    return role;
  }

  return 'assistant';
}

function extractTextContent(content: unknown): string {
  if (typeof content === 'string') {
    return content;
  }

  if (Array.isArray(content)) {
    return content
      .filter((block: any) => block?.type === 'text' && typeof block.text === 'string')
      .map((block: any) => block.text)
      .join('\n');
  }

  if (content && typeof content === 'object') {
    const maybeObject = content as Record<string, any>;

    if (typeof maybeObject.content === 'string') {
      return maybeObject.content;
    }

    if (Array.isArray(maybeObject.content)) {
      return extractTextContent(maybeObject.content);
    }

    if (typeof maybeObject.text === 'string') {
      return maybeObject.text;
    }
  }

  return '';
}

function looksLikeUnavailableError(error: unknown): boolean {
  const message = (error instanceof Error ? error.message : String(error ?? '')).toLowerCase();

  return (
    message.includes(COCO_AGENT_ID) ||
    message.includes('agent not found') ||
    message.includes('unknown agent') ||
    message.includes('session not found') ||
    message.includes('unknown session') ||
    message.includes('invalid session') ||
    message.includes('failed to create session') ||
    message.includes('not configured')
  );
}

/**
 * Derives the workspace-relative file path from the file editor state.
 * Returns null if no file is actively being viewed/edited.
 */
function deriveCurrentFilePath(
  view: string,
  selectedFile: { name: string } | null,
  selectedSourceType: string,
  selectedAgentId: string | null,
  selectedProjectName: string | null,
  currentPath: string,
  gameFilePath: string | null,
): string | null {
  // Only include context when actively viewing or editing a file
  if (view !== 'viewer' && view !== 'editor') {
    return null;
  }

  if (!selectedFile) {
    return null;
  }

  if (selectedSourceType === 'agent' && selectedAgentId) {
    return `agents/${selectedAgentId}/${selectedFile.name}`;
  }

  if (selectedSourceType === 'project' && selectedProjectName) {
    const pathPrefix = currentPath ? `${currentPath}/` : '';
    return `projects/${selectedProjectName}/${pathPrefix}${selectedFile.name}`;
  }

  if (selectedSourceType === 'game' && gameFilePath) {
    return `game/${gameFilePath}`;
  }

  return null;
}

export default function CocoChatBar() {
  const connected = useAppStore((state) => state.connected);
  const agents = useAppStore((state) => state.agents);
  const messages = useAppStore((state) => state.messages);
  const streamingMessage = useAppStore((state) => state.streamingMessage);
  const thinking = useAppStore((state) => state.thinking);
  const thinkingContent = useAppStore((state) => state.thinkingContent);
  const toolCalls = useAppStore((state) => state.toolCalls);
  const sessions = useAppStore((state) => state.sessions);
  const activeSessionKey = useAppStore((state) => state.activeSessionKey);
  const displayName = useSettingsStore((state) => state.settings.general.displayName);

  // File editor state for context awareness
  const fileEditorView = useFileEditorStore((state) => state.view);
  const selectedFile = useFileEditorStore((state) => state.selectedFile);
  const selectedSourceType = useFileEditorStore((state) => state.selectedSourceType);
  const selectedAgentId = useFileEditorStore((state) => state.selectedAgentId);
  const selectedProjectName = useFileEditorStore((state) => state.selectedProjectName);
  const currentPath = useFileEditorStore((state) => state.currentPath);
  const gameFilePath = useFileEditorStore((state) => state.gameFilePath);

  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [awaitingResponse, setAwaitingResponse] = useState(false);
  const [sessionUnavailable, setSessionUnavailable] = useState(false);
  const [agentCheckTimedOut, setAgentCheckTimedOut] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [pendingTokenWarningText, setPendingTokenWarningText] = useState<string | null>(null);
  const [pendingTokenWarningCount, setPendingTokenWarningCount] = useState<number | null>(null);

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const loadedHistoryKeysRef = useRef<Set<string>>(new Set());

  const cocoAgent = useMemo(
    () => agents.find((agent) => agent.id === COCO_AGENT_ID) ?? null,
    [agents],
  );

  const cocoEmoji = cocoAgent?.identity?.emoji ?? COCO_FALLBACK_EMOJI;
  const cocoName = cocoAgent?.identity?.name ?? COCO_FALLBACK_NAME;
  const cocoSubtitle = cocoAgent?.identity?.description?.trim() || 'Game Designer';

  const userIdentifier = useMemo(
    () => deriveDashboardUserIdentifier(displayName, activeSessionKey),
    [activeSessionKey, displayName],
  );
  const sessionKey = useMemo(() => createCocoSessionKey(userIdentifier), [userIdentifier]);

  // Derive the current file path for context injection
  const currentFilePath = useMemo(
    () =>
      deriveCurrentFilePath(
        fileEditorView,
        selectedFile,
        selectedSourceType,
        selectedAgentId,
        selectedProjectName,
        currentPath,
        gameFilePath,
      ),
    [fileEditorView, selectedFile, selectedSourceType, selectedAgentId, selectedProjectName, currentPath, gameFilePath],
  );
  const sessionMessages = messages[sessionKey] ?? [];
  const streamText = streamingMessage[sessionKey] ?? '';
  const isThinking = thinking[sessionKey] ?? false;
  const thinkingText = thinkingContent[sessionKey] ?? null;
  const sessionToolCalls = toolCalls[sessionKey] ?? [];

  // Filter to show only running tool calls for compact display
  const runningToolCalls = sessionToolCalls.filter((tc) => tc.status === 'running');
  const hasStreaming = streamText.trim().length > 0;
  const lastMessage = sessionMessages.length > 0 ? sessionMessages[sessionMessages.length - 1] : null;

  const loadHistoryForSession = useCallback(async (targetSessionKey: string) => {
    const client = gatewayRef.current;
    if (!client?.connected || !targetSessionKey || loadedHistoryKeysRef.current.has(targetSessionKey)) {
      return;
    }

    loadedHistoryKeysRef.current.add(targetSessionKey);

    const existingMessages = useAppStore.getState().messages[targetSessionKey] ?? [];
    if (existingMessages.length > 0) {
      return;
    }

    setHistoryLoading(true);

    try {
      const { messages: history } = await client.getHistory(targetSessionKey, 1000);
      const historyMessages: ChatMessage[] = history
        .map((historyMessage) => {
          const role = historyMessage?.role;
          if (role === 'tool' || role === 'toolResult' || role === 'tool_result') {
            return null;
          }

          const content = extractTextContent(historyMessage?.content).trim();
          if (!content || content.startsWith('[System Message]')) {
            return null;
          }

          return {
            role: toMessageRole(role),
            content,
            timestamp: historyMessage?.timestamp ? String(historyMessage.timestamp) : undefined,
            isHistory: true,
          };
        })
        .filter((message): message is ChatMessage => message !== null);

      useAppStore.getState().replaceMessages(targetSessionKey, historyMessages);
    } catch (error) {
      console.debug('[CocoChatBar] Unable to load Coco history:', error);
      loadedHistoryKeysRef.current.delete(targetSessionKey);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!connected || cocoAgent) {
      setAgentCheckTimedOut(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setAgentCheckTimedOut(true);
    }, 1200);

    return () => {
      window.clearTimeout(timer);
    };
  }, [cocoAgent, connected]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const scroller = scrollRef.current;
    if (!scroller) {
      return;
    }

    scroller.scrollTo({
      top: scroller.scrollHeight,
      behavior: 'smooth',
    });
  }, [awaitingResponse, hasStreaming, isOpen, isThinking, sessionMessages, streamText, thinkingText]);

  useEffect(() => {
    if (!awaitingResponse) {
      return;
    }

    if (hasStreaming || isThinking) {
      return;
    }

    if (lastMessage && lastMessage.role !== 'user') {
      setAwaitingResponse(false);
    }
  }, [awaitingResponse, hasStreaming, isThinking, lastMessage]);

  useEffect(() => {
    if (!connected || !isOpen) {
      return;
    }

    if (sessionMessages.length > 0) {
      loadedHistoryKeysRef.current.add(sessionKey);
      return;
    }

    void loadHistoryForSession(sessionKey);
  }, [connected, isOpen, loadHistoryForSession, sessionKey, sessionMessages.length]);

  const isCheckingAvailability = connected && !cocoAgent && !agentCheckTimedOut && !sessionUnavailable;
  const isUnavailable = sessionUnavailable || (connected && agentCheckTimedOut && !cocoAgent);
  const inputDisabled = !connected || isCheckingAvailability || isUnavailable;
  const showTypingBubble = awaitingResponse && !hasStreaming;
  const showActivityDot = awaitingResponse || hasStreaming || isThinking || runningToolCalls.length > 0;

  const handleOpen = () => {
    setIsOpen(true);
    if (connected && sessionMessages.length === 0) {
      void loadHistoryForSession(sessionKey);
    }
  };

  const clearTokenWarning = useCallback(() => {
    setPendingTokenWarningText(null);
    setPendingTokenWarningCount(null);
  }, []);

  useEffect(() => {
    clearTokenWarning();
  }, [clearTokenWarning, sessionKey]);

  const maybeOpenTokenWarning = useCallback((messageText: string) => {
    const tokenCount = getSessionTokenCount(sessions, sessionKey);
    if (!shouldShowContextTokenWarning({ messageText, hasAttachments: false, tokenCount })) {
      return false;
    }

    setPendingTokenWarningText(messageText);
    setPendingTokenWarningCount(tokenCount);
    return true;
  }, [sessionKey, sessions]);

  const handleSend = async (skipTokenWarning = false) => {
    const content = input.trim();
    if (!content || inputDisabled || sending) {
      return;
    }

    if (!skipTokenWarning && maybeOpenTokenWarning(content)) {
      return;
    }

    const client = gatewayRef.current;
    if (!client?.connected) {
      return;
    }

    const activeSessionKey = sessionKey;
    const store = useAppStore.getState();

    // Display the user's message without the context prefix
    store.addMessage(activeSessionKey, {
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    });

    // Prepend file context to the message sent to the backend (invisible to user)
    const messageToSend = currentFilePath
      ? `[Currently viewing: ${currentFilePath}]\n\n${content}`
      : content;

    setSending(true);
    setAwaitingResponse(true);

    try {
      await client.sendMessage(activeSessionKey, messageToSend);
      setInput('');

      void client.setSessionVerbose(activeSessionKey, 'full').catch((error) => {
        console.debug('[CocoChatBar] Unable to enable verbose mode for Coco session:', error);
      });
      void client.request('sessions.patch', { key: activeSessionKey, reasoningLevel: 'stream' }).catch((error) => {
        console.debug('[CocoChatBar] Unable to enable reasoning stream for Coco session:', error);
      });
    } catch (error) {
      store.addMessage(activeSessionKey, {
        role: 'system',
        content: toFriendlyErrorMessage(error, 'chat-send'),
        timestamp: new Date().toISOString(),
      });
      setAwaitingResponse(false);

      if (looksLikeUnavailableError(error)) {
        setSessionUnavailable(true);
      }
    } finally {
      setSending(false);
    }
  };

  const handleProceedTokenWarning = () => {
    if (pendingTokenWarningText === null) {
      return;
    }

    clearTokenWarning();
    void handleSend(true);
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSend();
    }
  };

  // Compact tool call status display for the chat bar
  const renderToolCallStatus = (entry: ToolCallEntry) => {
    const statusClass = `is-${entry.status}`;
    return (
      <div key={entry.toolCallId} className={`coco-tool-call ${statusClass}`}>
        <span className="coco-tool-name">{entry.name}</span>
        <span className={`coco-tool-status ${statusClass}`}>
          {entry.status === 'running' && (
            <>
              <span className="coco-tool-running-dot" />
              Running
            </>
          )}
          {entry.status === 'done' && '✓ Done'}
          {entry.status === 'error' && '✗ Error'}
        </span>
      </div>
    );
  };

  const placeholder = !connected
    ? 'Connect to the gateway to chat with Coco'
    : isCheckingAvailability
      ? 'Looking for Coco…'
      : isUnavailable
        ? "Coco isn't available right now"
        : 'Ask Coco about mechanics, loops, or balance…';

  return (
    <>
      <style>{COCO_CHAT_BAR_STYLES}</style>

      <div
        className={`coco-chat-backdrop${isOpen ? ' is-visible' : ''}`}
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />

      <div className="coco-chat-shell" aria-live="polite">
        <div className={`coco-chat-panel${isOpen ? ' is-open' : ''}`}>
          <div className="coco-chat-header">
            <div className="coco-chat-title">
              <div className="coco-chat-title-main">{cocoEmoji} {cocoName} — Game Designer</div>
              <div className="coco-chat-title-sub">{cocoSubtitle}</div>
            </div>

            <button
              type="button"
              className="coco-chat-fold-button"
              onClick={() => setIsOpen(false)}
              aria-label="Minimize Coco chat"
              title="Minimize"
            >
              ─
            </button>
          </div>

          <div ref={scrollRef} className="coco-chat-messages">
            {!connected ? (
              <div className="coco-chat-notice">Connect to the gateway to chat with Coco.</div>
            ) : isCheckingAvailability ? (
              <div className="coco-chat-notice">Looking for Coco…</div>
            ) : historyLoading ? (
              <div className="coco-chat-notice">Loading conversation…</div>
            ) : isUnavailable ? (
              <div className="coco-chat-notice">{COCO_UNAVAILABLE_MESSAGE}</div>
            ) : sessionMessages.length === 0 && !showTypingBubble && !hasStreaming ? (
              <div className="coco-chat-empty">
                <div className="coco-chat-empty-title">Quick design help</div>
                Ask Coco for mechanics ideas, progression tuning, reward loops, or feature critiques without leaving the dashboard.
              </div>
            ) : null}

            {sessionMessages.map((message, index) => {
              const isUser = message.role === 'user';
              const isSystem = message.role === 'system';
              const rowClass = isSystem ? 'is-system' : isUser ? 'is-user' : 'is-agent';
              const senderLabel = isSystem ? null : isUser ? 'You' : cocoName;

              return (
                <div key={`${message.timestamp ?? index}-${index}`} className={`coco-chat-row ${rowClass}`}>
                  <div className="coco-chat-bubble-wrap">
                    {senderLabel ? <div className="coco-chat-sender">{senderLabel}</div> : null}
                    <div className="coco-chat-bubble">
                      {isUser || isSystem ? (
                        <span style={{ whiteSpace: 'pre-wrap' }}>{message.content}</span>
                      ) : (
                        <MarkdownRenderer content={message.content} />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {isThinking && !hasStreaming ? (
              <div className="coco-chat-row is-agent">
                <div className="coco-chat-bubble-wrap">
                  <div className="coco-chat-sender">{cocoName}</div>
                  <div className="coco-chat-bubble" style={{ padding: 0 }}>
                    <ThinkingIndicator isThinking={isThinking} content={thinkingText} />
                  </div>
                </div>
              </div>
            ) : null}

            {runningToolCalls.length > 0 && !hasStreaming ? (
              <div className="coco-tool-calls">
                {runningToolCalls.map(renderToolCallStatus)}
              </div>
            ) : null}

            {showTypingBubble && runningToolCalls.length === 0 ? (
              <div className="coco-chat-row is-agent">
                <div className="coco-chat-bubble-wrap">
                  <div className="coco-chat-sender">{cocoName}</div>
                  <div className="coco-chat-bubble">
                    <div className="coco-chat-typing" aria-label="Coco is typing">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {hasStreaming ? (
              <div className="coco-chat-row is-agent">
                <div className="coco-chat-bubble-wrap">
                  <div className="coco-chat-sender">{cocoName}</div>
                  <div className="coco-chat-bubble">
                    <MarkdownRenderer content={streamText} isStreaming />
                    <span className="streaming-dot" />
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="coco-chat-composer">
            <textarea
              ref={inputRef}
              className="coco-chat-input"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={inputDisabled || sending}
              placeholder={placeholder}
            />

            <button
              type="button"
              className="coco-chat-send"
              onClick={() => void handleSend()}
              disabled={inputDisabled || sending || input.trim().length === 0}
            >
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>

        <button
          type="button"
          className={`coco-chat-pill${isOpen ? ' is-hidden' : ''}`}
          onClick={handleOpen}
          aria-label="Open Coco chat"
          title="Chat with Coco"
        >
          <span className="coco-chat-pill-emoji">{cocoEmoji}</span>
          <span className="coco-chat-pill-label">Coco</span>
          {showActivityDot ? <span className="coco-chat-pill-dot" /> : null}
        </button>
      </div>

      <ContextTokenWarningDialog
        tokenCount={pendingTokenWarningCount}
        onCancel={clearTokenWarning}
        onProceed={handleProceedTokenWarning}
      />
    </>
  );
}
